import * as React from 'react';
import { createContext, useContext, useRef, useCallback, useMemo } from 'react';
import { view, createViewConfig } from '@sail/engine';
import { message, useIntl } from '@sail/intl';
import { flag } from '@sail/icons/react/Icon';
import { useListBox, CollectionProvider, useListCollection, useReactAriaTreeState } from '@sail/react-aria';
import { ListBoxItem, ListBoxItemTransforms } from './ListBoxItem';
import { ListBoxGroup, ListBoxGroupTransforms } from './ListBoxGroup';
import { Icon } from './Icon';
import { ListBoxClearItem } from './ListBoxClearItem';
const noResultsMsg = message({
    id: '@sail/ui.listbox.noResults',
    description: 'A label for when there are no results in a list',
    defaultMessage: 'No results'
});
export const ListBoxEmptyState = ()=>{
    const { formatMessage } = useIntl();
    return /*#__PURE__*/ React.createElement(view.div, {
        css: {
            font: 'label.medium',
            marginX: 'xsmall',
            marginY: 'large',
            padding: 'small',
            color: 'subdued',
            textAlign: 'center'
        }
    }, /*#__PURE__*/ React.createElement(Icon, {
        size: "xsmall",
        icon: flag,
        css: {
            marginX: 'space.75'
        }
    }), ' ', formatMessage(noResultsMsg));
};
export const ListBoxConfig = createViewConfig({
    name: 'ListBox',
    props: {},
    defaults: {
        selectionMode: 'single',
        defaultExpandedKeys: 'all',
        expandable: false
    }
});
export const ListBoxContext = /*#__PURE__*/ createContext({});
const useListBoxState = (props)=>{
    const { state: inheritedState, collection: inheritedCollection, options: inheritedOptions } = useContext(ListBoxContext);
    // Track whether we're using inherited state or not so we can't violate the
    // rules of hooks by switching between the two.
    const isUsingInheritedState = useRef(Boolean(inheritedState && inheritedCollection));
    if (isUsingInheritedState.current) {
        return {
            state: inheritedState,
            collection: inheritedCollection,
            options: inheritedOptions
        };
    }
    // Create collection and based on the incoming props
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const collection = useListCollection({
        ...props,
        itemTransforms: ListBoxItemTransforms,
        groupTransforms: ListBoxGroupTransforms
    });
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const state = useReactAriaTreeState({
        getItems: collection.getItems,
        ...props
    });
    return {
        collection,
        state,
        options: {}
    };
};
export const ListBox = ListBoxConfig.createGenericView(({ data, item, group, children, label, onSelectionChange, selectionMode, defaultSelectedKeys, selectedKeys, disabledKeys, emptyState = /*#__PURE__*/ React.createElement(ListBoxEmptyState, null), expandedKeys, defaultExpandedKeys, onExpandedChange, expandable, clearable, ...props })=>{
    const { collection, state, options } = useListBoxState({
        data,
        item,
        group,
        children,
        selectionMode,
        onSelectionChange,
        defaultSelectedKeys,
        selectedKeys,
        disabledKeys,
        expandedKeys,
        defaultExpandedKeys,
        onExpandedChange,
        expandable
    });
    const filter = useCallback((item)=>{
        return Boolean((item === null || item === void 0 ? void 0 : item.key) && state.collection.getItem(item.key));
    }, [
        state
    ]);
    const isEmpty = useMemo(()=>collection.getItems().filter(filter).length === 0, [
        collection,
        filter
    ]);
    const ref = React.useRef(null);
    const { listBoxProps } = useListBox({
        label,
        shouldFocusWrap: true,
        ...options
    }, state, ref);
    return /*#__PURE__*/ React.createElement(CollectionProvider, {
        collection: collection,
        state: state
    }, /*#__PURE__*/ React.createElement(view.div, {
        ref: ref,
        ...props,
        ...listBoxProps
    }, clearable ? /*#__PURE__*/ React.createElement(ListBoxClearItem, null, clearable.label) : null, collection.render((props)=>/*#__PURE__*/ React.createElement(ListBoxItem, {
            key: props.id,
            ...props
        }, props.children || props.textValue), (props)=>/*#__PURE__*/ React.createElement(ListBoxGroup, {
            key: props.id,
            ...props
        }), filter), isEmpty && emptyState));
}, {
    css: {
        stack: 'y',
        gap: 'space.0',
        backgroundColor: 'surface',
        borderRadius: 'medium',
        keyline: 'border',
        keylineWidth: 'small',
        padding: 'xsmall',
        focusRing: 'none'
    }
});

//# sourceMappingURL=ListBox.js.map